/**
 * CallOverlayBar Component
 *
 * Pill-shaped top bar for in-progress call (dialing/ringing/active).
 * Shows caller info, status, optional matched-cases chip, and End/Answer/Expand buttons.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallIcon from '@mui/icons-material/Call';
import CallMadeIcon from '@mui/icons-material/CallMade';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';

import { ShortcutBadge } from '@/shared/components';

import type { QueueModel } from '../../domain/types';

// ============================================================================
// Types
// ============================================================================

export interface CallOverlayBarProps {
  queue: QueueModel;
  onAnswer: () => void;
  onEnd: () => void;
  onExpand: () => void;
  showActions?: boolean;
  showInCallControls?: boolean;
  isMuted?: boolean;
  isOnHold?: boolean;
  onToggleMute?: () => void;
  onToggleHold?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/** When is_incoming_call is true, treat as Incoming; otherwise Outbound. */
function getCallTypeLabel(queue: QueueModel): 'Incoming' | 'Outbound' {
  return queue.is_incoming_call ? 'Incoming' : 'Outbound';
}

function getCallStatusLabel(queue: QueueModel): string {
  switch (queue.status.toLowerCase()) {
    case 'ringing':
      return 'Ringing';
    case 'dialing':
      return 'Dialing';
    case 'active':
      return 'On Call';
    default:
      return 'Call';
  }
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    const first = parts[0]?.[0] ?? '';
    const last = parts[parts.length - 1]?.[0] ?? '';
    return `${first}${last}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/** Tooltip content pairing the action label with its keyboard shortcut. */
function shortcutTitle(label: string, shortcut: string) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span>{label}</span>
      <ShortcutBadge label={shortcut} />
    </Box>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CallOverlayBar({
  queue,
  onAnswer,
  onEnd,
  onExpand,
  showActions = true,
  showInCallControls = false,
  isMuted = false,
  isOnHold = false,
  onToggleMute,
  onToggleHold,
}: CallOverlayBarProps) {
  const navigate = useNavigate();
  const typeLabel = getCallTypeLabel(queue);
  const statusLabel = getCallStatusLabel(queue);
  const isOutbound = !queue.is_incoming_call;
  const associatedCases = queue.associated_cases ?? [];
  const matchedCount = associatedCases.length;

  const [casesAnchor, setCasesAnchor] = useState<HTMLElement | null>(null);
  const casesOpen = Boolean(casesAnchor);

  const handleCasesClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCasesAnchor(e.currentTarget);
  }, []);

  const handleCasesClose = useCallback(() => {
    setCasesAnchor(null);
  }, []);

  const handleCaseSelect = useCallback((caseId: number) => {
    handleCasesClose();
    navigate(`/case-detail/${caseId}`);
  }, [handleCasesClose, navigate]);

  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: 4,
        minHeight: 56,
        maxWidth: 560,
        width: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {/* Avatar + name + status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Avatar
          sx={{
            bgcolor: t => `linear-gradient(180deg, ${alpha(t.palette.success.main, 0.9)} 0%, ${alpha(t.palette.success.dark, 0.95)} 100%)`,
            color: 'common.black',
            fontWeight: 700,
          }}
        >
          {getInitials(queue.name)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {queue.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isOutbound ? <CallMadeIcon sx={{ fontSize: 14, color: 'primary.main' }} /> : <PhoneInTalkIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            <Typography variant="caption" color={isOutbound ? 'primary.main' : 'success.main'} fontWeight={500}>
              {typeLabel} · {statusLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {matchedCount > 0 && (
        <>
          <Chip
            size="small"
            label={`${String(matchedCount)} matched cases`}
            onClick={handleCasesClick}
            sx={{ bgcolor: 'action.hover', fontWeight: 500, cursor: 'pointer' }}
          />
          <Popover
            open={casesOpen}
            anchorEl={casesAnchor}
            onClose={handleCasesClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { minWidth: 200, maxHeight: 300 } } }}
          >
            <Box sx={{ py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600 }}>
                Matched Cases
              </Typography>
              {associatedCases.map(c => (
                <ListItemButton key={c.id} onClick={() => handleCaseSelect(c.id)} dense>
                  <ListItemText
                    primary={c.loan_id}
                    secondary={c.status}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))}
            </Box>
          </Popover>
        </>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* In-call controls (mute/hold) should be available even when showActions is false. */}
        {showInCallControls && (
          <>
            {!showActions && (
              <Tooltip title={shortcutTitle('End call', 'Alt+H')} placement="bottom">
                <IconButton
                  size="small"
                  onClick={onEnd}
                  aria-label="End call"
                  aria-keyshortcuts="Alt+H"
                  sx={{
                    bgcolor: t => alpha(t.palette.error.main, 0.15),
                    color: 'error.main',
                    '&:hover': { bgcolor: t => alpha(t.palette.error.main, 0.25) },
                  }}
                >
                  <CallEndIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={shortcutTitle(isMuted ? 'Unmute microphone' : 'Mute microphone', 'Alt+M')} placement="bottom">
              <span>
                <IconButton
                  size="small"
                  onClick={onToggleMute}
                  disabled={!onToggleMute}
                  aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  aria-keyshortcuts="Alt+M"
                  sx={{
                    bgcolor: t => alpha(t.palette.info.main, 0.12),
                    color: 'info.main',
                    '&:hover': { bgcolor: t => alpha(t.palette.info.main, 0.2) },
                  }}
                >
                  {isMuted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={shortcutTitle(isOnHold ? 'Resume call' : 'Hold call', 'Alt+P')} placement="bottom">
              <span>
                <IconButton
                  size="small"
                  onClick={onToggleHold}
                  disabled={!onToggleHold}
                  aria-label={isOnHold ? 'Resume call' : 'Hold call'}
                  aria-keyshortcuts="Alt+P"
                  sx={{
                    bgcolor: t => alpha(t.palette.warning.main, 0.12),
                    color: 'warning.main',
                    '&:hover': { bgcolor: t => alpha(t.palette.warning.main, 0.2) },
                  }}
                >
                  {isOnHold ? <PlayCircleOutlineIcon fontSize="small" /> : <PauseCircleOutlineIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        {showActions && (
          <>
            <Tooltip title={shortcutTitle('End call', 'Alt+H')} placement="bottom">
              <IconButton
                size="small"
                onClick={onEnd}
                aria-label="End call"
                aria-keyshortcuts="Alt+H"
                sx={{
                  bgcolor: t => alpha(t.palette.error.main, 0.15),
                  color: 'error.main',
                  '&:hover': { bgcolor: t => alpha(t.palette.error.main, 0.25) },
                }}
              >
                <CallEndIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={shortcutTitle('Answer call', 'Alt+A')} placement="bottom">
              <IconButton
                size="small"
                onClick={onAnswer}
                aria-label="Answer call"
                aria-keyshortcuts="Alt+A"
                sx={{
                  bgcolor: t => alpha(t.palette.success.main, 0.15),
                  color: 'success.main',
                  '&:hover': { bgcolor: t => alpha(t.palette.success.main, 0.25) },
                }}
              >
                <CallIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <IconButton
          size="small"
          onClick={onExpand}
          aria-label="Expand to modal"
          sx={{
            bgcolor: 'action.hover',
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
}
